import { Predicate } from '.'

/**
 * A collection of type guards used to help validate
 * the type of an argument at run-time while guaranteeing
 * the typescript type is valid
 *
 * https://www.typescriptlang.org/docs/handbook/advanced-types.html#type-guards-and-differentiating-types
 */
export class Predicates {
  public static isString(arg: unknown): arg is string {
    return typeof arg === 'string'
  }

  public static isNumber(arg: unknown): arg is number {
    return typeof arg === 'number'
  }

  public static isBoolean(arg: unknown): arg is boolean {
    return typeof arg === 'boolean'
  }

  public static isUndefined(arg: unknown): arg is undefined {
    return typeof arg === 'undefined'
  }

  public static isNull(arg: unknown): arg is null {
    return arg === null
  }

  public static isArray(arg: unknown): arg is [] {
    return Array.isArray(arg)
  }

  public static isObject(arg: unknown): arg is object {
    return Object.prototype.toString.call(arg) === '[object Object]'
  }

  public static getLiteralStringPredicate<T extends string>(
    validLiterals: T[]
  ): Predicate<T> {
    return (arg: unknown): arg is T =>
      Predicates.isString(arg) && validLiterals.indexOf(arg as T) > -1
  }

  /**
   * Returns a Predicate that validates an argument is an object
   * with a set of properties that each pass a defined predicate
   *
   * @param propertyPredicates A set of predicates that defines if
   * an object is of a specific type by checking if the argument has
   * a property that passes the specified predicate
   *
   * Example:
   *
   * interface Dog {
   *   readonly name: string
   *   readonly age: number
   *   readonly owner: string
   * }
   *
   * const isDog: Predicate<Dog> = Predicates.hasShape({
   *   name: Predicates.isString,
   *   age: Predicates.isNumber,
   *   owner: Predicates.or(Predicates.isString, Predicates.isUndefined)
   * })
   */
  public static hasShape<T extends object>(propertyPredicates: {
    readonly [P in keyof T]?: Predicate<T[P]>
  }): Predicate<T> {
    return (arg: unknown): arg is T => {
      if (Predicates.isObject(arg)) {
        for (const property in propertyPredicates) {
          if (propertyPredicates[property] !== undefined) {
            const predicate = propertyPredicates[property]
            if (predicate && !predicate(arg[property as string])) {
              return false
            }
          }
        }
        return true
      }
      return false
    }
  }

  /**
   * Returns a Predicate that validates an argument is an array of items
   * that passes the given predicate
   *
   * @param predicate Predicate that every item in the array must pass
   *
   * Example:
   *
   * function isEven(arg: unknown): arg is number {
   *   return typeof arg === 'number' && arg % 2 === 0
   * }
   *
   * function getMaxIfAllEven(values: unknown): number {
   *
   *   if(Predicates.every(isEven)(values)) {
   *     return Math.max(values)
   *   }
   *
   *   return -1;
   * }
   */
  public static every<T = unknown>(predicate: Predicate<T>): Predicate<T[]> {
    return (arg: unknown): arg is T[] => {
      return Predicates.isArray(arg) && arg.every((item) => predicate(item))
    }
  }

  public static or<T>(...predicates: Array<Predicate<T>>): Predicate<T> {
    return (arg: unknown): arg is T =>
      predicates.some((predicate) => predicate(arg))
  }
}

/**
 * Convenience methods for validating run-time types
 */
export class Preconditions {
  public static defaultErrorMessage: string = 'Argument type is invalid'

  public static checkArgument<T>(
    predicate: Predicate<T>,
    arg: unknown,
    argumentName?: string
  ): T {
    if (predicate(arg)) {
      return arg
    }

    throw new TypeError(
      argumentName
        ? `Argument type of ${argumentName} is invalid`
        : Preconditions.defaultErrorMessage
    )
  }
}
